import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import MESSAGES from '../../../../common/data/constants/messages';
import { ICostCenter } from '../../../../common/data/dtos/coster-center';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardFooterRight, CardTabItem } from '../../../../components/bootstrap/Card';
import FormGroup from '../../../../components/bootstrap/forms/FormGroup';
import Input from '../../../../components/bootstrap/forms/Input';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../../../components/bootstrap/Modal';
import Spinner from '../../../../components/bootstrap/Spinner';
import { showSuccess, showWarning } from '../../../../components/extras/Notifications';
import costCenterService from '../../../../services/cost-center-service';

type CostCenterFormValues = {
	id?: string;
	description: string;
};

interface Props {
	isModalOpened: boolean;
	selectedCostCenter: ICostCenter | null;
	onModalClose: () => void;
	onAfterSubmit: () => void;
}

export const CostCenterModal = ({ isModalOpened, selectedCostCenter, onModalClose, onAfterSubmit }: Props) => {
	const formik = useFormik({
		initialValues: {
			description: selectedCostCenter?.description || '',
		},
		validate: (values) => {
			const errors: {
				description?: string;
			} = {};

			if (!values.description) {
				errors.description = MESSAGES.VALIDATION.REQUIRED_FIELD;
			}

			return errors;
		},
		onSubmit: (values, { resetForm }) => {
			onFormSubmit(values);
			resetForm();
		},
	});

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	useEffect(() => {
		resetForm();

		if (selectedCostCenter) {
			setFieldValues(selectedCostCenter);
		}
	}, [isModalOpened]);

	const onFormSubmit = async (values: CostCenterFormValues) => {
		if (selectedCostCenter) {
			setIsSubmitting(true);

			const response = await costCenterService.updateCostCenter({
				id: selectedCostCenter.id,
				description: values.description,
				rowVersion: selectedCostCenter.rowVersion,
				status: selectedCostCenter.status,
			});

			setIsSubmitting(false);

			if (response.error) {
				showWarning(MESSAGES.ACTION.EDIT_ERROR);
				return;
			}

			showSuccess(MESSAGES.ACTION.EDITED);
		} else {
			setIsSubmitting(true);

			const response = await costCenterService.createCompany({
				description: values.description,
			});

			setIsSubmitting(false);

			if (response.error) {
				showWarning(MESSAGES.ACTION.SAVE_ERROR);
				return;
			}

			showSuccess(MESSAGES.ACTION.SAVED);
		}

		resetForm();
		onAfterSubmit();
	};

	const onClose = () => {
		onModalClose();
	};

	const resetForm = () => {
		formik.resetForm({
			values: {
				description: ''
			},
		});
	};

	const setFieldValues = (values: ICostCenter) => {
		formik.setFieldValue('description', values.description || '');
	};

	return (
		<Modal isOpen={isModalOpened} setIsOpen={onClose} size={'lg'} isCentered isStaticBackdrop>
			<ModalHeader setIsOpen={onClose}>
				<ModalTitle id='company-modal'>Manutenção de centros de custo</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Card hasTab tabButtonColor='primary'>
					<CardTabItem id='tab-item-1' title='Dados do centro de custo' icon='Store'>
						<div className='row g-4'>
							<div className='col-lg-12 col-md-12 col-sm-12'>
								<FormGroup id='description' label='Descrição' isFloating>
									<Input
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={formik.values.description}
										isValid={formik.isValid}
										isTouched={formik.touched.description}
										invalidFeedback={formik.errors.description}
										validFeedback=''
									/>
								</FormGroup>
							</div>
						</div>
					</CardTabItem>
				</Card>
			</ModalBody>
			<ModalFooter className='bg-transparent'>
				<CardFooterRight>
					<Button color='success' className='w-100' onClick={formik.handleSubmit} isDisable={isSubmitting}>
						{isSubmitting && <Spinner isSmall inButton isGrow />}
						Salvar
					</Button>
				</CardFooterRight>
			</ModalFooter>
		</Modal>
	);
};
